'use client';

import { Heading as ChakraHeading } from '@chakra-ui/react';
import type { HeadingProps as ChakraHeadingProps } from '@chakra-ui/react';
import { forwardRef } from 'react';

export interface HeadingProps
  extends Omit<
    ChakraHeadingProps,
    // Remove shorthand props
    | 'w'
    | 'h'
    | 'p'
    | 'pt'
    | 'pr'
    | 'pb'
    | 'pl'
    | 'px'
    | 'py'
    | 'm'
    | 'mt'
    | 'mr'
    | 'mb'
    | 'ml'
    | 'mx'
    | 'my'
  > {
  // Typography props
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  fontSize?: ChakraHeadingProps['fontSize'];
  fontWeight?: ChakraHeadingProps['fontWeight'];
  lineHeight?: ChakraHeadingProps['lineHeight'];
  letterSpacing?: ChakraHeadingProps['letterSpacing'];
  textAlign?: ChakraHeadingProps['textAlign'];
  color?: ChakraHeadingProps['color'];
  // Layout props with full names
  width?: ChakraHeadingProps['width'];
  margin?: ChakraHeadingProps['margin'];
  marginTop?: ChakraHeadingProps['marginTop'];
  marginBottom?: ChakraHeadingProps['marginBottom'];
  padding?: ChakraHeadingProps['padding'];
}

/**
 * Heading component for rendering semantic HTML heading elements.
 *
 * @example
 * <Heading as="h1" size="xl" color="#111827">
 *   Page Title
 * </Heading>
 *
 * @example
 * <Heading as="h2" size="lg" marginBottom={4}>
 *   Section Title
 * </Heading>
 */
export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>((props, ref) => {
  return <ChakraHeading ref={ref} {...props} />;
});

Heading.displayName = 'Heading';
